/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import * as Msdyn365 from '@msdyn365-commerce/core';
import { IGridSettings, IImageSettings } from '@msdyn365-commerce/core';
import React from 'react';

export interface IStickyProductHeaderProps {
    isSticky: boolean;
    stickyRef: React.RefObject<HTMLDivElement>;
    baseImageUrl: string;
    productImageUrl: string | undefined;
    fallbackImageUrl: string | undefined;
    productName: string | undefined;
    productTitle: string;
    priceComponent: React.ReactNode;
    gridSettings: IGridSettings;
    imageSettings: IImageSettings;
    // Delivery method props
    isISO: boolean;
    onlyCNCAvailable: boolean;
    isEcomProductAvailable: boolean;
    isCnCProductAvailable: boolean | undefined;
    isHomeDelTileSelected: boolean;
    isCNCTileSelected: boolean;
    onSelectHomeDelivery: () => void;
    onSelectCnC: () => void;
    // Add to cart
    addToCartContainer: React.ReactNode;
    addedToCartModal: React.ReactNode;
    // Navigation links
    carouselHeading?: string;
    carouselHeadingHref?: string;
    hideYmlUnbxd: boolean | undefined;
    unbxdRecommendationsCount: number;
}

const StickyProductHeader: React.FC<IStickyProductHeaderProps> = ({
    isSticky,
    stickyRef,
    baseImageUrl,
    productImageUrl,
    fallbackImageUrl,
    productName,
    productTitle,
    priceComponent,
    gridSettings,
    imageSettings,
    isISO,
    onlyCNCAvailable,
    isEcomProductAvailable,
    isCnCProductAvailable,
    isHomeDelTileSelected,
    isCNCTileSelected,
    onSelectHomeDelivery,
    onSelectCnC,
    addToCartContainer,
    addedToCartModal,
    carouselHeading,
    carouselHeadingHref,
    hideYmlUnbxd,
    unbxdRecommendationsCount
}) => {
    const handleProductDetailsClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        const hash = '#product-details';
        window.history.replaceState(null, '', hash);
        document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const handleDeliveryReturnsClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        const id = 'delivery-returns';
        const el = document.getElementById(id);
        if (el) {
            el.dispatchEvent(new CustomEvent('openAccordion', { bubbles: true }));
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    return (
        <div ref={stickyRef} className={`sticky-product-detail ${isSticky ? 'sticky' : ''}`}>
            <div className='container'>
                <div className='product-detail'>
                    <div className='image'>
                        <Msdyn365.Image
                            src={`${baseImageUrl}${productImageUrl}` || ''}
                            fallBackSrc={fallbackImageUrl}
                            altText={productName}
                            gridSettings={gridSettings}
                            imageSettings={imageSettings}
                            loadFailureBehavior='empty'
                        />
                    </div>
                    <div className='detail'>
                        <div className='title-price'>
                            <div className='title'>{productTitle}</div>
                            {priceComponent}
                        </div>
                        <div className='delivery-add-to-cart'>
                            <div
                                className={`delivery-methods ${
                                    !isCnCProductAvailable && !isISO ? 'only-hd-available' : onlyCNCAvailable ? 'only-in-store' : ''
                                }`}
                            >
                                {!isISO && (
                                    <div
                                        className={`home ${!isEcomProductAvailable ? 'disabled' : ''}`}
                                        onClick={() => {
                                            if (!isEcomProductAvailable) {
                                                return;
                                            }
                                            onSelectHomeDelivery();
                                        }}
                                    >
                                        <input
                                            type='radio'
                                            id='home'
                                            name='delivery-method'
                                            value='home'
                                            checked={isHomeDelTileSelected}
                                            disabled={!isEcomProductAvailable}
                                            readOnly
                                        />
                                        <span className='checkmark'></span>
                                        <label htmlFor='home'>Home Delivery</label>
                                    </div>
                                )}

                                {(isISO || isCnCProductAvailable) && (
                                    <div
                                        className={`cnc ${!isCnCProductAvailable ? 'disabled' : ''}`}
                                        onClick={() => {
                                            if (!isCnCProductAvailable) {
                                                return;
                                            }
                                            onSelectCnC();
                                        }}
                                    >
                                        <input
                                            type='radio'
                                            id='cnc'
                                            name='delivery-method'
                                            value='cnc'
                                            checked={isCNCTileSelected}
                                            disabled={!isCnCProductAvailable}
                                            readOnly
                                        />
                                        <span className='checkmark'></span>
                                        <label htmlFor='cnc'>Free Click & Collect</label>
                                    </div>
                                )}
                            </div>
                            <div className='add-to-cart'>{addToCartContainer}</div>
                            {addedToCartModal}
                        </div>
                    </div>
                </div>
            </div>
            <div className='links'>
                <div className='container'>
                    <ul>
                        <li>
                            <a href='#product-details' onClick={handleProductDetailsClick}>
                                Product Details
                            </a>
                        </li>
                        <li>
                            <a href='#delivery-returns' onClick={handleDeliveryReturnsClick}>
                                Delivery &amp; returns
                            </a>
                        </li>
                        {unbxdRecommendationsCount > 0 && !hideYmlUnbxd && carouselHeading && (
                            <li>
                                <a href={`#${carouselHeadingHref}`}>{carouselHeading}</a>
                            </li>
                        )}
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default StickyProductHeader;
